'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

export default function FAQSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      category: 'general',
      question: 'What is Signaly and how does it work?',
      answer: 'Signaly is an AI-driven trading signal platform that analyzes market data 24/7 to provide actionable trading signals. Our advanced algorithms process multiple factors including technical indicators, market sentiment, and historical patterns to generate signals with 87% average confidence.'
    },
    {
      category: 'general',
      question: 'How accurate are the trading signals?',
      answer: 'Our signals maintain a 67% win rate with an average confidence level of 87%. However, past performance does not guarantee future results. We recommend using proper risk management and never investing more than you can afford to lose.'
    },
    {
      category: 'technical',
      question: 'Which platforms are supported?',
      answer: 'Signaly is available on iOS (App Store) and Android (Google Play). We also offer a web dashboard for monitoring signals and managing your account. All platforms sync in real-time.'
    },
    {
      category: 'technical',
      question: 'Do I need trading experience to use Signaly?',
      answer: 'No! Signaly is designed for traders of all levels. Our "no fuss, no long training" approach means you can start following signals immediately. We provide clear entry/exit points, stop-loss levels, and risk management guidance.'
    },
    {
      category: 'billing',
      question: 'What subscription plans are available?',
      answer: 'We offer three plans: Free (basic signals, limited features), Pro ($29/month with advanced signals and analytics), and Premium ($79/month with all features, priority support, and API access). All paid plans include a 7-day free trial.'
    },
    {
      category: 'billing',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time from your account settings. You will continue to have access until the end of your billing period. We also offer a 30-day money-back guarantee for new subscribers.'
    },
    {
      category: 'technical',
      question: 'How do I receive signal notifications?',
      answer: 'Signals are delivered via push notifications on mobile, email alerts, and in-app notifications. You can customize notification preferences in your account settings to receive only the signals that match your trading strategy.'
    },
    {
      category: 'general',
      question: 'Is my data and trading information secure?',
      answer: 'Absolutely. We use bank-level encryption (SSL/TLS), are SOC 2 compliant, and never store your exchange API keys or trading credentials. All data is encrypted at rest and in transit.'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Questions' },
    { value: 'general', label: 'General' },
    { value: 'technical', label: 'Technical' },
    { value: 'billing', label: 'Billing' }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 lg:p-8">
        <div className="h-8 bg-muted rounded w-48 mb-6 animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 lg:p-8">
      <h2 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-6">
        Frequently Asked Questions
      </h2>

      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map(category => (
          <button
            key={category.value}
            onClick={() => setActiveCategory(category.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              activeCategory === category.value
                ? 'bg-primary text-primary-foreground shadow-cyan-glow'
                : 'bg-muted text-text-secondary hover:bg-muted/80'
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredFAQs.map((faq, index) => (
          <div
            key={index}
            className="border border-border rounded-lg overflow-hidden transition-all duration-200 hover:border-primary/50"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-200"
            >
              <span className="text-base font-semibold text-foreground pr-4">
                {faq.question}
              </span>
              <Icon
                name="ChevronDownIcon"
                size={20}
                className={`text-primary flex-shrink-0 transition-transform duration-200 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
              />
            </button>
            {openIndex === index && (
              <div className="px-6 pb-4 pt-2 bg-muted/30">
                <p className="text-text-secondary leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-border">
        <p className="text-center text-text-secondary">
          Can&apos;t find what you&apos;re looking for?{' '}
          <a href="#contact-form" className="text-primary hover:underline font-medium">
            Contact our support team
          </a>
        </p>
      </div>
    </div>
  );
}